import React, { useEffect } from "react";
import MindMePaymentsLogo from "../../../../../../images/logos/mindme-payments-logo.png";
import MerchantAccountStore from "../MerchantAccountStore";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import { MerchantBlocks, MerchantWrapper } from "./Merchant.styles";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  RedButton,
  SapphireBlueButton,
  SingleSelect,
  withRouter,
  CreatableSelect as SelectCreateable,
  CoreInputModelUtil,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  AddressInputModel,
  ApplicationVolumeDetails,
  BusinessDetails,
  CountryWithStateModel,
  CreateBusinessInputModel,
  MerchantAccountDetails,
  MerchantApplicationInputModel,
  ModeOfTransactions,
  PrimaryContactDetails,
  SelectedItemModel,
} from "../../../shared/models";
import { MainOuterCard } from "../../../shared/styles";
import { CountryPhoneDropdown } from "../../../shared/components";
import { CountryStore, ItemsStore, RolesAndPermissionStore } from "../../../shared/store";

const businessCategoryOptions: IOption[] = [
  { value: "RETAIL", label: "Retail" },
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "MOTO", label: "Moto" },
  { value: "ECOMMERCE", label: "E-commerce" },
  { value: "LODGING", label: "Lodging" },
  { value: "CAR_RENTAL", label: "Car Rental" },
  { value: "PETROLEUM", label: "Petroleum" },
  { value: "QSR", label: "QSR" },
];

const ownershipTypeOptions: IOption[] = [
  { value: "SOLO_TRADER", label: "Sole Proprietorship / Individual" },
  {
    value: "LIMITED_LIABILITY_COMPANY",
    label: "Limited Liability Company (LLC)",
  },
  { value: "PRIVATE", label: "Private Corporation" },
  { value: "PARTNERSHIP", label: "Partnership" },
  { value: "PUBLIC", label: "Public Corporation" },
  { value: "TAX_EXEMPT_ORGANIZATION", label: "Tax Exempt" },
  { value: "GOVERNMENT", label: "Government" },
];

type ModelKeys =
  | keyof MerchantApplicationInputModel
  | keyof CreateBusinessInputModel
  | keyof PrimaryContactDetails
  | keyof MerchantAccountDetails
  | keyof AddressInputModel
  | keyof ApplicationVolumeDetails
  | keyof ModeOfTransactions;

type IProps = {
  merchantAccountStore: MerchantAccountStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  itemStore: ItemsStore; 
};

const MerchantAccountSignUp: React.FC<IProps> = ({
  merchantAccountStore,
  navigate,
  rolesAndPermissionStore,
  itemStore, 
}) => {
  useEffect(() => {
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/merchant-accounts",
        title: "New Merchant Application",
      })
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, []);


  useEffect(() => {
    merchantAccountStore.initNewApplicationData(itemStore.countryStore);
  }, [itemStore.countryStore.countryDataLoaded]);

  useEffect(() => {
    merchantAccountStore.fetchBusinesses();
    merchantAccountStore.fetchMCCCodes();
    merchantAccountStore.resetApplicationData(itemStore.countryStore);
  }, []);

  const existingBusinesses = (): IOption[] => {
    return (
      merchantAccountStore.businesses.map((business: BusinessDetails) => ({
        label: business.name,
        value: business.id,
      })) || []
    );
  };

  const handleInputChange = (
    field: keyof MerchantApplicationInputModel,
    value: string
  ) => {
    merchantAccountStore.updateApplicationData({
      ...merchantAccountStore.applicationData,
      [field]: value,
    } as MerchantApplicationInputModel);
  };

  const handleNameChange = (
    field: keyof CreateBusinessInputModel,
    value: string
  ) => {
    merchantAccountStore.updateBusinessData({
      ...merchantAccountStore.businessData,
      [field]: value,
      businessNameError: "",
    } as CreateBusinessInputModel);
  };

  const handleNestedInputChange = (
    nestedField: ModelKeys,
    field: ModelKeys,
    value: string
  ) => {
    let updatedValue = value;
    if (field === "online") {
      const onlineValue = CoreInputModelUtil.isValidNumber(parseFloat(value))
        ? parseInt(value, 10)
        : 0;
      const inPersonValue = 100 - onlineValue;
      updatedValue = onlineValue.toString();
      merchantAccountStore.updateApplicationData({
        ...merchantAccountStore.applicationData,
        [nestedField]: {
          ...(merchantAccountStore.applicationData[
            nestedField as keyof MerchantApplicationInputModel
          ] as any),
          [field]: updatedValue,
          inPerson: inPersonValue.toString(),
        },
      } as MerchantApplicationInputModel);
    } else {
      if (field === "taxId") {
        updatedValue = UtilityMethods.formatTaxId(value);
      }
      merchantAccountStore.updateApplicationData({
        ...merchantAccountStore.applicationData,
        [nestedField]: {
          ...(merchantAccountStore.applicationData[
            nestedField as keyof MerchantApplicationInputModel
          ] as any),
          [field]: field === "taxId" ? updatedValue : value,
        },
      } as MerchantApplicationInputModel);
    }
  };

  const handleBusinessDataInputChange = (
    nestedField: keyof CreateBusinessInputModel,
    field: keyof PrimaryContactDetails,
    value: string
  ) => {
    merchantAccountStore.updateBusinessData({
      ...merchantAccountStore.businessData,
      [nestedField]: {
        ...(merchantAccountStore.businessData[
          nestedField as keyof CreateBusinessInputModel
        ] as any),
        [field]: value,
      },
    } as CreateBusinessInputModel);
  };

  const handleCountryDropdownChange = (
    nestedField: string,
    field: string,
    value: CountryWithStateModel
  ) => {
    if (nestedField === "primaryContactDetails") {
      merchantAccountStore.updateBusinessData({
        ...merchantAccountStore.businessData,
        [nestedField]: {
          ...(merchantAccountStore.businessData[
            nestedField as keyof CreateBusinessInputModel
          ] as any),
          [field]: value,
        },
      } as CreateBusinessInputModel);
    } else {
      merchantAccountStore.updateApplicationData({
        ...merchantAccountStore.applicationData,
        [nestedField]: {
          ...(merchantAccountStore.applicationData[
            nestedField as keyof MerchantApplicationInputModel
          ] as any),
          [field]: value,
        },
      } as MerchantApplicationInputModel);
    }
  };

  const handleSelect = (id: string | null, fieldName: string) => {
    if (id) {
      const country = itemStore.countryStore.countriesWithState.find(
        (country) => country.id === id
      );
      if (country) {
        handleCountryDropdownChange(fieldName, "countryDetails", country);
      }
    }
  };

  const onClickCreateNew = () => {
    if (!rolesAndPermissionStore.hasManagePermission("Create")) return;
    merchantAccountStore.resetBusinessData(itemStore.countryStore);
    merchantAccountStore.toggleBusinessState();
  };

  const selectedCategories = (): IOption[] => {
    if (!merchantAccountStore.applicationData.businessName) return [];
    return [
      {
        label: merchantAccountStore.applicationData.businessName,
        value: merchantAccountStore.applicationData.businessName,
      },
    ];
  };

  const onCreateOrSelectCategory = (newValue: IOption) => {
    if (!newValue) {
      handleInputChange("businessId", "");
      handleInputChange("businessName", "");
      return;
    }
    if (newValue) {
      if (newValue.__isNew__) {
        merchantAccountStore.toggleBusinessState();
        handleNameChange("businessName", newValue.label);
      } else {
        handleInputChange("businessName", newValue.label);
        handleInputChange("businessId", newValue.value);
      }
    }
  };

  const onSingleSelectChange = (
    field: keyof MerchantAccountDetails,
    value: string
  ) => {
    handleNestedInputChange(
      "accountDetails" as keyof MerchantApplicationInputModel,
      field,
      value
    );
  };

  const textInput = (
    type: string,
    value: string,
    placeHolder: string,
    className: string,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void,
    error: boolean,
    errorMessage: string,
    fieldName?: ModelKeys
  ) => {
    return (
      <Input
        type={type as "text" | "textarea"}
        value={value}
        error={error}
        errorMessage={errorMessage}
        className={`styled-input merchant-inputs ${className}`}
        onChange={onChange}
        placeholder={placeHolder}
        disabled={fieldName === "inPerson" || fieldName === "total"}
      />
    );
  };

  const singleSelect = (
    field: keyof MerchantAccountDetails,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string,
    emptySelection?: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => onSingleSelectChange(field, option.value)}
        className={className}
        emptySelection={emptySelection}
      />
    );
  };

  const onBusinessFormCancel = () => {
    merchantAccountStore.toggleBusinessState();
    merchantAccountStore.resetBusinessData(itemStore.countryStore);
  };

  const onSave = async () => {
    const id = await merchantAccountStore.submitApplication();
    if (id) {
      navigate(`/app/merchant-accounts/success/${id}`);
    } else {
      console.log("Error in creating merchant account");
    }
    merchantAccountStore.updateFormSubmissionState(false);
  };

  const onSubmit = () => {
    merchantAccountStore.submitNewBusiness();
  };

  const onCancel = () => {
    merchantAccountStore.resetApplicationData(itemStore.countryStore);
    navigate("/app/merchant-accounts");
  };

  const renderHeader = () => {
    return (
      <FlexContainer alignItems="center" justifyContent="space-between">
        <img src={MindMePaymentsLogo} className="mindme-payment-icon" />
        <CustomLabel
          fontSize="30px"
          fontWeight="600"
          color="#000"
          textAlign="center"
        >
          New Merchant
          <br /> Application
        </CustomLabel>
        <CustomLabel
          fontSize="12px"
          textAlign="right"
          className="desc-merchant-form"
        >
          Complete a short form below to apply for a processing account and
          start taking payments online.
        </CustomLabel>
      </FlexContainer>
    );
  };

  const renderNewBusinessForm = () => {
    return (
      <div>
        <Input
          type="text"
          value={merchantAccountStore.businessData.businessName}
          error={
            merchantAccountStore.businessData.hasError("businessName") ||
            merchantAccountStore.businessData.businessNameError.length > 0
          }
          className={"styled-input merchant-inputs"}
          onChange={(e) => handleNameChange("businessName", e.target.value)}
          placeholder="Business Name"
          errorMessage={merchantAccountStore.businessData.businessNameError}
          maxLength={100}
        />

        <MerchantBlocks className="rows-margin-bottom">
          <CustomLabel className="merchant-subheader">
            Primary Contact
          </CustomLabel>
          <FlexContainer>
            {textInput(
              "text",
              merchantAccountStore.businessData.primaryContactDetails
                .emailAddress,
              "Email address",
              "",
              (e) =>
                handleBusinessDataInputChange(
                  "primaryContactDetails",
                  "emailAddress",
                  e.target.value
                ),
              merchantAccountStore.businessData.primaryContactDetails.hasError(
                "emailAddress"
              ),
              merchantAccountStore.businessData.primaryContactDetails.getErrorMessage(
                "emailAddress"
              )
            )}
          </FlexContainer>
          <FlexContainer
            alignItems="center"
            spacing={20}
            className="merchant-phone"
          >
            <CountryPhoneDropdown
              selectedCountry={
                merchantAccountStore.businessData.primaryContactDetails
                  .countryDetails
              }
              error={
                merchantAccountStore.businessData.primaryContactDetails.hasError(
                  "phoneNumber"
                ) ||
                merchantAccountStore.businessData.primaryContactDetails.hasError(
                  "countryDetails"
                )
              }
              errorMessage={
                "please enter 10 digit phone number and select country"
              }
              onCountryChange={(id: string) =>
                handleSelect(id, "primaryContactDetails")
              }
              phoneNumber={
                merchantAccountStore.businessData.primaryContactDetails
                  .phoneNumber
              }
              onPhoneNumberChange={(number: string) =>
                handleBusinessDataInputChange(
                  "primaryContactDetails",
                  "phoneNumber",
                  number
                )
              }
              availableCountries={
                itemStore.countryStore.countriesWithState
              }
              className="country-dropdown-expanded"
            />
          </FlexContainer>
          <FlexContainer alignItems="center" spacing={20}>
            {textInput(
              "text",
              merchantAccountStore.businessData.primaryContactDetails.firstName,
              "First Name",
              "",
              (e) =>
                handleBusinessDataInputChange(
                  "primaryContactDetails",
                  "firstName",
                  e.target.value
                ),
              merchantAccountStore.businessData.primaryContactDetails.hasError(
                "firstName"
              ),
              merchantAccountStore.businessData.primaryContactDetails.getErrorMessage(
                "firstName"
              )
            )}
            {textInput(
              "text",
              merchantAccountStore.businessData.primaryContactDetails.lastName,
              "Last Name",
              "",
              (e) =>
                handleBusinessDataInputChange(
                  "primaryContactDetails",
                  "lastName",
                  e.target.value
                ),
              merchantAccountStore.businessData.primaryContactDetails.hasError(
                "lastName"
              ),
              merchantAccountStore.businessData.primaryContactDetails.getErrorMessage(
                "lastName"
              )
            )}
          </FlexContainer>
        </MerchantBlocks>
      </div>
    );
  };

  const renderBusinessSection = () => {
    return (
      <MerchantBlocks>
        <CustomLabel className="merchant-header-description">
          BUSINESS
        </CustomLabel>
        <CustomLabel fontSize="12px" lineHeight="14px" margin="0px 0px 30px">
          Select an existing business or add a new one
        </CustomLabel>
        {!merchantAccountStore.isNewBusiness ? (
          <FlexContainer spacing={20}>
            <SelectCreateable
              isClearable={true}
              isMulti={false}
              options={existingBusinesses()}
              selectedOptions={selectedCategories()}
              onSingleChange={(newValue) => onCreateOrSelectCategory(newValue)}
              className="option-category-select "
              classPrefix="option-select"
              error={merchantAccountStore.applicationData.hasError(
                "businessName"
              )}
              errorMessage={merchantAccountStore.applicationData.getErrorMessage(
                "businessName"
              )}
            />
            <SapphireBlueButton
              bgColor="#fff"
              borderColor="#556bb8"
              color="#556bb8"
              height="40px"
              onClick={() => onClickCreateNew()}
            >
              Create New
            </SapphireBlueButton>
          </FlexContainer>
        ) : (
          renderNewBusinessForm()
        )}
      </MerchantBlocks>
    );
  };

  const renderAccountSection = () => {
    return (
      <MerchantBlocks>
        <CustomLabel className="merchant-header">ACCOUNT</CustomLabel>
        {textInput(
          "text",
          merchantAccountStore.applicationData.accountName,
          "Account Name",
          "",
          (e) => handleInputChange("accountName", e.target.value),
          merchantAccountStore.applicationData.hasError("accountName"),
          merchantAccountStore.applicationData.getErrorMessage("accountName")
        )}
      </MerchantBlocks>
    );
  };

  const renderAccountDetailsSection = () => {
    const mccCodeOptions = merchantAccountStore.mccCodes.map((mccCode) => ({
      label: `${mccCode.code} - ${mccCode.industry}`,
      value: mccCode.code,
    }));
    return (
      <MerchantBlocks className="rows-margin-bottom">
        <CustomLabel className="merchant-header">DETAILS</CustomLabel>
        <CustomLabel className="merchant-subheader">
          Account Details
        </CustomLabel>
        <FlexContainer alignItems="center" spacing={20}>
          {singleSelect(
            "ownershipType" as keyof MerchantAccountDetails,
            merchantAccountStore.applicationData.accountDetails.ownershipType,
            "",
            ownershipTypeOptions,
            merchantAccountStore.applicationData.accountDetails.hasError(
              "ownershipType"
            ),
            merchantAccountStore.applicationData.accountDetails.getErrorMessage(
              "ownershipType"
            ),
            "Select Ownership Type"
          )}
          {singleSelect(
            "businessCategory" as keyof MerchantAccountDetails,
            merchantAccountStore.applicationData.accountDetails
              .businessCategory,
            "",
            businessCategoryOptions,
            merchantAccountStore.applicationData.accountDetails.hasError(
              "businessCategory"
            ),
            merchantAccountStore.applicationData.accountDetails.getErrorMessage(
              "businessCategory"
            ),
            "Select Type"
          )}
        </FlexContainer>
        <FlexContainer alignItems="center" spacing={20}>
          {textInput(
            "text",
            merchantAccountStore.applicationData.accountDetails
              .legalNameOfBusiness,
            "Legal Name of Business",
            "",
            (e) =>
              handleNestedInputChange(
                "accountDetails",
                "legalNameOfBusiness",
                e.target.value
              ),
            merchantAccountStore.applicationData.accountDetails.hasError(
              "legalNameOfBusiness"
            ),
            merchantAccountStore.applicationData.accountDetails.getErrorMessage(
              "legalNameOfBusiness"
            )
          )}
          {textInput(
            "text",
            merchantAccountStore.applicationData.accountDetails.DBA || "",
            "DBA Name (optional)",
            "",
            (e) =>
              handleNestedInputChange("accountDetails", "DBA", e.target.value),
            merchantAccountStore.applicationData.accountDetails.hasError("DBA"),
            merchantAccountStore.applicationData.accountDetails.getErrorMessage(
              "DBA"
            )
          )}
        </FlexContainer>
        <FlexContainer alignItems="center" spacing={20}>
          {textInput(
            "text",
            merchantAccountStore.applicationData.accountDetails.taxId,
            "Tax ID (EIN)",
            "",
            (e) =>
              handleNestedInputChange(
                "accountDetails",
                "taxId",
                e.target.value
              ),
            merchantAccountStore.applicationData.accountDetails.hasError(
              "taxId"
            ),
            merchantAccountStore.applicationData.accountDetails.getErrorMessage(
              "taxId"
            )
          )}
          {singleSelect(
            "mccCode" as keyof MerchantAccountDetails,
            merchantAccountStore.applicationData.accountDetails.mccCode,
            "select-overflowed-ellipsis",
            mccCodeOptions,
            merchantAccountStore.applicationData.accountDetails.hasError(
              "mccCode"
            ),
            merchantAccountStore.applicationData.accountDetails.getErrorMessage(
              "mccCode"
            ),
            "Select Category"
          )}
        </FlexContainer>
        <CountryPhoneDropdown
          selectedCountry={
            merchantAccountStore.applicationData.accountDetails.countryDetails
          }
          error={
            merchantAccountStore.applicationData.accountDetails.hasError(
              "phoneNumber"
            ) ||
            merchantAccountStore.applicationData.accountDetails.hasError(
              "countryDetails"
            )
          }
          errorMessage={"please enter 10 digit phone number and select country"}
          onCountryChange={(id: string) => handleSelect(id, "accountDetails")}
          phoneNumber={
            merchantAccountStore.applicationData.accountDetails.phoneNumber
          }
          onPhoneNumberChange={(number: string) =>
            handleNestedInputChange("accountDetails", "phoneNumber", number)
          }
          availableCountries={
            itemStore.countryStore.countriesWithState
          }
          className="country-dropdown-expanded"
        />
        {textInput(
          "text",
          merchantAccountStore.applicationData.accountDetails.website,
          "Website",
          "",
          (e) =>
            handleNestedInputChange(
              "accountDetails",
              "website",
              e.target.value
            ),
          merchantAccountStore.applicationData.accountDetails.hasError(
            "website"
          ),
          merchantAccountStore.applicationData.accountDetails.getErrorMessage(
            "website"
          )
        )}
      </MerchantBlocks>
    );
  };

  const renderBusinessAddress = () => {
    return (
      <MerchantBlocks className="rows-margin-bottom">
        <CustomLabel className="merchant-subheader">
          Business Address
        </CustomLabel>
        <CountryPhoneDropdown
          selectedCountry={
            merchantAccountStore.applicationData.address.countryDetails
          }
          error={
            merchantAccountStore.applicationData.address.hasError(
              "phoneNumber"
            ) ||
            merchantAccountStore.applicationData.address.hasError(
              "countryDetails"
            )
          }
          errorMessage={"please enter 10 digit phone number and select country"}
          onCountryChange={(id: string) => handleSelect(id, "address")}
          phoneNumber={merchantAccountStore.applicationData.address.phoneNumber}
          onPhoneNumberChange={(number: string) =>
            handleNestedInputChange("address", "phoneNumber", number)
          }
          availableCountries={
            itemStore.countryStore.countriesWithState
          }
          className="country-dropdown-expanded"
        />
        {textInput(
          "text",
          merchantAccountStore.applicationData.address.address1,
          "Address 1",
          "",
          (e) => handleNestedInputChange("address", "address1", e.target.value),
          merchantAccountStore.applicationData.address.hasError("address1"),
          merchantAccountStore.applicationData.address.getErrorMessage(
            "address1"
          )
        )}
        {textInput(
          "text",
          merchantAccountStore.applicationData.address.city,
          "City",
          "",
          (e) => handleNestedInputChange("address", "city", e.target.value),
          merchantAccountStore.applicationData.address.hasError("city"),
          merchantAccountStore.applicationData.address.getErrorMessage("city")
        )}
        {textInput(
          "text",
          merchantAccountStore.applicationData.address.zip,
          "ZIP Code",
          "",
          (e) => handleNestedInputChange("address", "zip", e.target.value),
          merchantAccountStore.applicationData.address.hasError("zip"),
          merchantAccountStore.applicationData.address.getErrorMessage("zip")
        )}
      </MerchantBlocks>
    );
  };

  const renderVolumeSection = () => {
    return (
      <MerchantBlocks className="rows-margin-bottom">
        <FlexContainer className="no-margin-bottom" spacing={50}>
          <div>
            <CustomLabel className="merchant-subheader">Volume</CustomLabel>
          </div>
          <div>
            <CustomLabel className="merchant-subheader">Location</CustomLabel>
          </div>
        </FlexContainer>
        <FlexContainer
          alignItems="center"
          spacing={50}
          className="volume-and-location"
        >
          <div>
            {textInput(
              "text",
              merchantAccountStore.applicationData.volumeDetails
                .avgAnnualVolume,
              "Average Annual Volume",
              "",
              (e) =>
                handleNestedInputChange(
                  "volumeDetails",
                  "avgAnnualVolume",
                  e.target.value
                ),
              merchantAccountStore.applicationData.volumeDetails.hasError(
                "avgAnnualVolume"
              ),
              merchantAccountStore.applicationData.volumeDetails.getErrorMessage(
                "avgAnnualVolume"
              )
            )}
          </div>
          <div>
            <FlexContainer alignItems="center" spacing={15}>
              <div>
                {textInput(
                  "text",
                  merchantAccountStore.applicationData.modeOfTransactions
                    .online,
                  "",
                  "",
                  (e) =>
                    handleNestedInputChange(
                      "modeOfTransactions",
                      "online",
                      e.target.value
                    ),
                  merchantAccountStore.applicationData.modeOfTransactions.hasError(
                    "online"
                  ),
                  merchantAccountStore.applicationData.modeOfTransactions.getErrorMessage(
                    "online"
                  )
                )}
              </div>
              <CustomLabel>Online</CustomLabel>
            </FlexContainer>
          </div>
        </FlexContainer>
        <FlexContainer
          alignItems="center"
          spacing={50}
          className="volume-and-location"
        >
          <div>
            {textInput(
              "text",
              merchantAccountStore.applicationData.volumeDetails
                .avgTicketAmount,
              "Average Ticket Amount",
              "",
              (e) =>
                handleNestedInputChange(
                  "volumeDetails",
                  "avgTicketAmount",
                  e.target.value
                ),
              merchantAccountStore.applicationData.volumeDetails.hasError(
                "avgTicketAmount"
              ),
              merchantAccountStore.applicationData.volumeDetails.getErrorMessage(
                "avgTicketAmount"
              )
            )}
          </div>
          <div>
            <FlexContainer alignItems="center" spacing={15}>
              {textInput(
                "text",
                merchantAccountStore.applicationData.modeOfTransactions
                  .inPerson,
                "",
                "",
                (e) =>
                  handleNestedInputChange(
                    "modeOfTransactions",
                    "inPerson",
                    e.target.value
                  ),
                false,
                "",
                "inPerson" as keyof ModeOfTransactions
              )}
              <CustomLabel>In Person</CustomLabel>
            </FlexContainer>
          </div>
        </FlexContainer>
        <FlexContainer
          alignItems="center"
          spacing={50}
          className="volume-and-location"
        >
          <div>
            {textInput(
              "text",
              merchantAccountStore.applicationData.volumeDetails
                .highestTicketAmount,
              "Highest Ticket Amount",
              "",
              (e) =>
                handleNestedInputChange(
                  "volumeDetails",
                  "highestTicketAmount",
                  e.target.value
                ),
              merchantAccountStore.applicationData.volumeDetails.hasError(
                "highestTicketAmount"
              ),
              merchantAccountStore.applicationData.volumeDetails.getErrorMessage(
                "highestTicketAmount"
              )
            )}
          </div>
          <div>
            <FlexContainer alignItems="center" spacing={15}>
              {textInput(
                "text",
                merchantAccountStore.applicationData.modeOfTransactions.total,
                "",
                "",
                (e) =>
                  handleNestedInputChange(
                    "modeOfTransactions",
                    "total",
                    e.target.value
                  ),
                false,
                ""
              )}
              <CustomLabel>Total</CustomLabel>
            </FlexContainer>
          </div>
        </FlexContainer>

        {/* <div className="location">
                      <h2>Location</h2>
                      <div className="location-item">
                         
                          <span></span>
                      </div>
                      <div className="location-item">
                         
                          <span>In Person</span>
                      </div>
                      <div className="location-item">
                         
                          <span>Total</span>
                      </div>
                  </div> */}
        {/* </div> */}
      </MerchantBlocks>
    );
  };

  return (
    <MerchantWrapper>
      <MainOuterCard>
        {renderHeader()}
        {renderBusinessSection()}
        {!merchantAccountStore.isNewBusiness ? (
          <>
            {renderAccountSection()}
            {renderAccountDetailsSection()}
            {renderBusinessAddress()}
            {renderVolumeSection()}

            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={20}
              padding="80px 0px 0px"
            >
              <RedButton
                bgColor="#fff"
                borderColor="#556bb8"
                color="#556bb8"
                height="40px"
                onClick={onCancel}
              >
                Cancel
              </RedButton>
              <CustomButton
                bgColor="#556bb8"
                borderColor="#556bb8"
                color="#fff"
                height="40px"
                onClick={() => onSave()}
                disabled={merchantAccountStore.isSaveInProgress}
              >
                Save Application
              </CustomButton>
            </FlexContainer>
          </>
        ) : (
          <FlexContainer spacing={20}>
            <CustomButton
              bgColor="#556bb8"
              borderColor="#556bb8"
              color="#fff"
              height="40px"
              onClick={() => onSubmit()}
              disabled={merchantAccountStore.isBusinessSubmission}
            >
              Submit
            </CustomButton>
            <RedButton height="40px" onClick={() => onBusinessFormCancel()}>
              Cancel
            </RedButton>
          </FlexContainer>
        )}
      </MainOuterCard>
    </MerchantWrapper>
  );
};

export default withRouter(observer(MerchantAccountSignUp));
